/**
 * Hook to detect the duration of an audio or video file.
 *
 * Works with both a File object and a pre-existing URL string.
 * When a File is supplied, a temporary object URL is created and
 * revoked after metadata is loaded so there are no memory leaks.
 *
 * Media type detection order:
 * 1. If `mediaType` is passed explicitly, it is used.
 * 2. Otherwise the MIME type of the File (or the URL extension) determines
 *    whether an <audio> or <video> element is used.
 *    Files with type "video/*" use a video element; everything else uses audio.
 *
 * @example
 * ```tsx
 * const { duration, loading } = useMediaDuration(selectedFile);
 * // or
 * const { duration, loading } = useMediaDuration(previewUrl, 'audio');
 * ```
 */

import { useState, useEffect } from 'react';

export type MediaElementType = 'audio' | 'video';

export interface UseMediaDurationResult {
  /** Duration in seconds, or null if not yet determined */
  duration: number | null;
  /** True while the media element is loading metadata */
  loading: boolean;
}

/**
 * Determines whether to use an audio or video element.
 * Falls back to 'audio' when detection is ambiguous.
 */
function inferMediaType(source: File | Blob | string, hint?: MediaElementType): MediaElementType {
  if (hint !== undefined) return hint;

  if (source instanceof Blob) {
    return source.type.startsWith('video/') ? 'video' : 'audio';
  }

  // URL string – use extension as heuristic
  const lower = source.toLowerCase().split('?')[0];
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.ogv', '.m4v'];
  const isVideo = videoExtensions.some((ext) => lower.endsWith(ext));
  return isVideo ? 'video' : 'audio';
}

/**
 * Returns the duration of the given media source in seconds.
 *
 * @param source - A File/Blob or a URL string. Pass null to reset state.
 * @param mediaType - Optional explicit hint. When omitted, type is inferred
 *                    from the MIME type (File) or file extension (URL string).
 */
export function useMediaDuration(
  source: File | Blob | string | null,
  mediaType?: MediaElementType
): UseMediaDurationResult {
  const [duration, setDuration] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (source === null) {
      setDuration(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let objectURL: string | null = null;

    setLoading(true);
    setDuration(null);

    // Resolve a URL we can assign to the media element's src
    const resolvedURL = typeof source === 'string' ? source : URL.createObjectURL(source);

    if (typeof source !== 'string') {
      objectURL = resolvedURL;
    }

    const effectiveType = inferMediaType(source, mediaType);

    const element: HTMLAudioElement | HTMLVideoElement =
      effectiveType === 'video' ? document.createElement('video') : new Audio();

    const handleLoaded = () => {
      if (cancelled) return;
      const d = element.duration;
      if (d && !isNaN(d) && isFinite(d)) {
        setDuration(d);
      }
      setLoading(false);
      if (objectURL !== null) {
        URL.revokeObjectURL(objectURL);
        objectURL = null;
      }
    };

    const handleError = () => {
      if (cancelled) return;
      setLoading(false);
      if (objectURL !== null) {
        URL.revokeObjectURL(objectURL);
        objectURL = null;
      }
    };

    element.addEventListener('loadedmetadata', handleLoaded);
    element.addEventListener('error', handleError);
    element.src = resolvedURL;
    // Trigger load without playing
    element.load();

    return () => {
      cancelled = true;
      element.removeEventListener('loadedmetadata', handleLoaded);
      element.removeEventListener('error', handleError);
      element.src = '';
      if (objectURL !== null) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [source, mediaType]);

  return { duration, loading };
}
